import { isQualifiedColocationFallbackClaim } from '../grounding/quantitySourceRoles';
import { isIsolatedFromOutOfScopeWaterLoad } from './waterUseScreeningGuard';
import { validateSemanticFactBinding } from './semanticFactBinding';
/**
 * QA SEVERITY POLICY — what actually fails a report (2026-08-05).
 *
 * ─── Why this exists ──────────────────────────────────────────────────
 *
 * The analysis hard gate was `containsBlockSeverity(report.qaFindings)`: ANY
 * finding anywhere in `qaFindings` carrying `severity: 'block'` failed the
 * report. That is an all-or-nothing bar over ~8 detectors, several with
 * systematic false-positive modes — so in practice no report could pass. The
 * live 2026-08-04 Redhead run carried 29 blocks; six of them named sections
 * that were present and populated, four called distinct quantities (550 MW
 * campus total vs 50 MW phase-1) a "conflict", and two flagged scope notes
 * ("this timeline does not include construction") as unevidenced absence
 * claims. A perfect narrative could not have cleared that gate.
 *
 * ─── The policy (William, 2026-08-05) ─────────────────────────────────
 *
 * BLOCK on demonstrable UNTRUTH only. A report that states something false —
 * a value that contradicts the evidence, an invented number or entity, an
 * internal self-contradiction, an unevidenced absence claim, or a confidence
 * band the evidence does not support — is not investment-grade and must not
 * be decision-usable.
 *
 * WARN on everything else: depth, drift, distribution, citation structure,
 * provider coverage, voice, action clustering, missing uncertainty labels.
 * These are quality signals, not falsehoods. A report carrying only warnings
 * is decision-current and surfaces its caveats in a banner — the way a human
 * analyst ships a report with noted limitations.
 *
 * The three general-purpose truth detectors with known false-positive modes
 * (`internalNumberFindings`, `absenceAssertionAudit`, `caveatHonesty`) stay
 * in the BLOCKING tier deliberately: they detect real truth violations, so
 * the fix is to make them accurate rather than to demote them. Narrow,
 * deterministic domain guards (currently `waterUseScreening`) may also opt in
 * when they carry the same locatable evidence. Their semantics are hardened
 * alongside this policy.
 *
 * ─── Discipline ───────────────────────────────────────────────────────
 *
 * PURE + total: no I/O, never throws, unknown detectors default to
 * NON-blocking (a new detector cannot silently start failing every report;
 * it must opt in here deliberately).
 */

/**
 * Detector buckets (the top-level `qaFindings` keys) whose block-severity
 * findings assert that the report says something UNTRUE.
 */
export const TRUTH_VIOLATION_DETECTORS: readonly string[] = [
  // Report contradicts itself about the same quantity.
  'internalNumberFindings',
  // Report asserts something is absent with no evidence of absence.
  'absenceAssertionAudit',
  // Report claims a confidence band its evidence does not support.
  'caveatHonesty',
  // Water-resource report invents a subject-site rate outside the
  // deterministic SED screen or upgrades a modeled endpoint to actual/
  // permitted/measured/requested/design use.
  'waterUseScreening',
  // Exact source scope/subject contradicts a reader-visible site claim.
  'sourceRoleTruth',
];

/**
 * Grounding violation kinds that are truth violations. The grounding gate
 * emits several kinds into one bucket, so it is classified per-kind:
 *   - value-mismatch   — states a value the canonical facts contradict
 *   - invented-number  — states a value for a fact the evidence never set
 *   - invented-entity  — names an entity the evidence never established
 * Deliberately NOT blocking: `unlabeled-estimate` (a missing uncertainty
 * qualifier is a labelling defect, not a false statement — the provenance
 * prompt rules target it), `dead-pointer` / `orphan-reference` (citation
 * structure), `invented-entity-hallucination` (advisory NER, already warn).
 */
export const TRUTH_VIOLATION_GROUNDING_KINDS: readonly string[] = [
  'value-mismatch',
  'invented-number',
  'invented-entity',
];

/**
 * A finding may only BLOCK if it can demonstrate its claim.
 *
 * "Blocks only on demonstrable untruth" is the gate's whole premise, so a
 * finding that cannot show its work has not met it. Two shapes qualify:
 *
 *  - LOCATABLE — a verbatim span from the report (`excerpt`, `sentenceText`,
 *    `claimText`, or per-mention excerpts) so a human can find the sentence.
 *  - CONTRADICTORY — both sides of the conflict (`observed` vs `expected`, or
 *    two or more `conflictingValues`) so the disagreement is visible without
 *    trusting the detector's prose.
 *
 * The detectors already produce this; only the orchestrator's 5-field
 * `FullAuditFinding` discarded it. The rule exists so a NEW detector, or one
 * that regresses into emitting bare messages, degrades to a warning instead of
 * killing reports on an assertion nobody can check.
 *
 * NOTE: this does not fix a finding whose evidence is real but MISATTRIBUTED —
 * a grounding `value-mismatch` comparing a fiber distance against the
 * interconnection distance shows observed-vs-expected honestly and still
 * blocks. Wrong-attribution is a detector-accuracy problem, not an evidence
 * one.
 */
export function hasDemonstrableEvidence(finding: unknown): boolean {
  if (!finding || typeof finding !== 'object') return false;
  const row = finding as Record<string, unknown>;

  const nonEmpty = (v: unknown): boolean => typeof v === 'string' && v.trim().length > 0;

  // Locatable: a quoted span from the narrative.
  if (nonEmpty(row.excerpt) || nonEmpty(row.sentenceText) || nonEmpty(row.claimText)) return true;
  if (
    Array.isArray(row.mentions)
    && row.mentions.some((m) => nonEmpty((m as Record<string, unknown> | null)?.excerpt))
  ) return true;

  // Contradictory: the disputed value plus what it disagrees with.
  //
  // `expected` is deliberately NOT required. An `invented-number` violation
  // fires precisely because the fact is ABSENT, so it has no expected value —
  // naming the fact the quoted figure violates is the whole demonstration
  // ("the report asserts 350 MW for a quantity the evidence never set").
  // Demanding both would demote the best-evidenced case this gate has.
  if (row.observed != null && (row.expected != null || row.factId != null)) return true;
  if (Array.isArray(row.conflictingValues) && row.conflictingValues.length >= 2) return true;

  return false;
}

/** Blocks that were downgraded for lack of evidence — surfaced, never silent. */
export interface EvidenceDowngrade {
  detector: string;
  message: string;
}

/** Is this detector/kind in the blocking tier at all, evidence aside? */
function isInBlockingTier(detectorKey: string, finding: unknown): boolean {
  if (!finding || typeof finding !== 'object') return false;
  const row = finding as Record<string, unknown>;
  return detectorKey === 'grounding'
    ? TRUTH_VIOLATION_GROUNDING_KINDS.includes(String(row.kind))
    : TRUTH_VIOLATION_DETECTORS.includes(detectorKey);
}

type FloodZoneBinding = 'A' | 'X' | 'TOTAL';

function extractZoneBindingsForValue(val: number, contextText: string): Set<FloodZoneBinding> {
  const bindings = new Set<FloodZoneBinding>();
  if (!Number.isFinite(val) || val <= 0 || !contextText) return bindings;

  const matchesVal = (raw: string): boolean => {
    const parsed = Number(raw.replace(/,/g, ''));
    return Number.isFinite(parsed) && Math.abs(parsed - val) <= 0.05;
  };

  const totalDenomRe =
    /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:-?\s*acres?)\s+(?:(?:study|site|parcel|property|campus|gross)\s+)?(?:polygon|area|boundary|site|parcel)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = totalDenomRe.exec(contextText)) !== null) {
    if (matchesVal(m[1]!)) {
      bindings.add('TOTAL');
    }
  }
  const totalPrefixRe =
    /\btotal\s+(?:(?:study|site|parcel|property|campus|gross)\s+)?(?:polygon|area|boundary|site|parcel|acreage)?\s*(?:of\s+|is\s+)?(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:-?\s*acres?)\b/gi;
  while ((m = totalPrefixRe.exec(contextText)) !== null) {
    if (matchesVal(m[1]!)) {
      bindings.add('TOTAL');
    }
  }

  const numThenParenZoneRe =
    /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:gross\s+)?acres?\s*\(\s*(?:fema\s+)?zone\s*(a(?:e|h|o)?|x)\b[^()]*\)/gi;
  while ((m = numThenParenZoneRe.exec(contextText)) !== null) {
    if (matchesVal(m[1]!)) {
      bindings.add(/^x/i.test(m[2]!) ? 'X' : 'A');
    }
  }
  const zoneThenParenNumRe =
    /\bzone\s*(a(?:e|h|o)?|x)\b[^()]*\(\s*(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:gross\s+)?acres?\b[^()]*\)/gi;
  while ((m = zoneThenParenNumRe.exec(contextText)) !== null) {
    if (matchesVal(m[2]!)) {
      bindings.add(/^x/i.test(m[1]!) ? 'X' : 'A');
    }
  }

  const stripped = contextText.replace(/\([^()]*\)/g, ' ');
  const clauses = stripped.split(
    /(?<!\d)\.(?!\d)|;|,\s*(?:whereas|while|but|however|yet|and\b|or\b)|\b(?:whereas|while|versus|vs\.?|and)\b/i,
  );
  for (const clause of clauses) {
    const numRe = /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:gross\s+)?acres?\b/gi;
    let hasTargetVal = false;
    while ((m = numRe.exec(clause)) !== null) {
      if (matchesVal(m[1]!)) {
        hasTargetVal = true;
        break;
      }
    }
    if (!hasTargetVal) continue;
    if (/\bzone\s*a(?:e|h|o)?\b/i.test(clause)) {
      bindings.add('A');
    }
    if (/\bzone\s*x\b/i.test(clause)) {
      bindings.add('X');
    }
  }

  return bindings;
}

function areComplementaryFloodZones(
  b1: ReadonlySet<'A' | 'X' | 'TOTAL'>,
  b2: ReadonlySet<'A' | 'X' | 'TOTAL'>,
): boolean {
  return (
    (b1.has('A') && !b1.has('X') && b2.has('X') && !b2.has('A')) ||
    (b1.has('X') && !b1.has('A') && b2.has('A') && !b2.has('X'))
  );
}

/** True when a single finding is a blocking truth violation that earns its block. */
export function isComplementaryFloodAcreageSplit(
  values: readonly number[],
  contextText: string,
): boolean {
  const finite = values.filter((n) => Number.isFinite(n) && n > 0);
  if (finite.length < 2) return false;

  const distinctValues = [...new Set(finite.map((v) => Number(v.toFixed(4))))];
  const zoneABoundValues = distinctValues.filter((v) => {
    const b = extractZoneBindingsForValue(v, contextText);
    return b.has('A') && !b.has('X') && !b.has('TOTAL');
  });
  const zoneXBoundValues = distinctValues.filter((v) => {
    const b = extractZoneBindingsForValue(v, contextText);
    return b.has('X') && !b.has('A') && !b.has('TOTAL');
  });
  if (zoneABoundValues.length >= 2 || zoneXBoundValues.length >= 2) {
    return false;
  }

  if (finite.length === 3) {
    const sorted = [...finite].sort((a, b) => a - b);
    const [p1, p2, tot] = sorted;
    if (
      p1 !== undefined &&
      p2 !== undefined &&
      tot !== undefined &&
      Math.abs(p1 + p2 - tot) <= 0.25 &&
      areComplementaryFloodZones(
        extractZoneBindingsForValue(p1, contextText),
        extractZoneBindingsForValue(p2, contextText),
      )
    ) {
      return true;
    }
    return false;
  }
  if (finite.length !== 2) return false;
  const [v1, v2] = finite;
  if (v1 === undefined || v2 === undefined || Math.abs(v1 - v2) < 1e-6) return false;

  const b1 = extractZoneBindingsForValue(v1, contextText);
  const b2 = extractZoneBindingsForValue(v2, contextText);
  if (areComplementaryFloodZones(b1, b2)) {
    return true;
  }
  const part = Math.min(v1, v2);
  const total = Math.max(v1, v2);
  const bPart = part === v1 ? b1 : b2;
  const bTotal = total === v1 ? b1 : b2;
  const pctRe = /(\d+(?:,\d{3})*(?:\.\d+)?)\s*acres\s*\(\s*(\d+(?:\.\d+)?)\s*%/gi;
  let m: RegExpExecArray | null;
  while ((m = pctRe.exec(contextText)) !== null) {
    const a = Number(m[1]!.replace(/,/g, ''));
    const pct = Number(m[2]!);
    if (!Number.isFinite(a) || !Number.isFinite(pct)) continue;
    if (Math.abs(a - part) < 0.05) {
      // Recognize "44.65 acres (7.8% of the 572.78-acre study polygon) in FEMA Zone A" where
      // the smaller value is bound to a flood zone and the larger value is explicitly labeled
      // as the total site/study polygon denominator (not flood-affected).
      const expectedTotalPct = (part / total) * 100;
      const denomInParensRe = new RegExp(
        '\\(\\s*' + m[2]! + '\\s*%\\s+of\\s+(?:the\\s+)?[\\d,]+(?:\\.\\d+)?\\s*(?:-?\\s*acres?)\\s+(?:(?:study|site|parcel|property|campus)\\s+)?(?:polygon|area|boundary|site|parcel)',
        'i',
      );
      if (
        (bPart.has('A') || bPart.has('X')) &&
        bTotal.has('TOTAL') &&
        Math.abs(expectedTotalPct - pct) <= 0.25 &&
        denomInParensRe.test(contextText)
      ) {
        return true;
      }
    }
  }
  return false;
}

export function isTruthViolation(detectorKey: string, finding: unknown): boolean {
  if (!finding || typeof finding !== 'object') return false;
  const row = finding as Record<string, unknown>;
  if (row.severity !== 'block') return false;

  if (!isInBlockingTier(detectorKey, row)) return false;

  const mentionExcerpts = Array.isArray(row.mentions)
    ? row.mentions
        .map((m) => {
          const rec = m as Record<string, unknown> | null;
          return [rec?.excerpt, rec?.sentenceScope, rec?.scopeText]
            .filter((s) => typeof s === 'string' && s.length > 0)
            .join(' ');
        })
        .join(' ')
    : '';
  const quoted = String(row.sentenceText ?? row.claimText ?? row.excerpt ?? mentionExcerpts ?? '');
  if (detectorKey === 'sourceRoleTruth' && isQualifiedColocationFallbackClaim(quoted)) {
    return false;
  }
  if (
    detectorKey === 'internalNumberFindings' &&
    row.canonicalKey === 'flood_area_acres'
  ) {
    const conflictingNums = Array.isArray(row.conflictingValues)
      ? row.conflictingValues.map(Number).filter(Number.isFinite)
      : [];
    if (isComplementaryFloodAcreageSplit(conflictingNums, quoted)) {
      return false;
    }
  }
  if (
    detectorKey === 'waterUseScreening' &&
    row.kind === 'water-band-out-of-scope-load' &&
    isIsolatedFromOutOfScopeWaterLoad(
      String(row.excerpt ?? ''),
      String(row.observed ?? ''),
      `${String(row.expected ?? '')} ${String(row.message ?? '')}`,
    )
  ) {
    return false;
  }
  if (detectorKey === 'grounding') {
    const factId = String(row.factId ?? '');
    if (
      (factId === 'connectivity.sourceRole' || factId === 'connectivity.data.sourceRole') &&
      isQualifiedColocationFallbackClaim(quoted)
    ) {
      return false;
    }
    if (
      factId === 'siteArea.acres' &&
      /\(\s*\d+(?:\.\d+)?\s*%\s*\)\s+in\s+zone\s+[ax]\b/i.test(quoted)
    ) {
      return false;
    }
    if (
      factId === 'grid.queueYears' &&
      !validateSemanticFactBinding({
        factId,
        claimText: quoted,
        observedText: typeof row.observed === 'string' ? row.observed : undefined,
        expectedText: typeof row.expected === 'string' ? row.expected : undefined,
        message: String(row.message ?? ''),
      }).ok
    ) {
      return false;
    }
  }

  // Earn the block by showing the evidence.
  return hasDemonstrableEvidence(finding);
}

/** Recursively collect block-severity findings under one detector bucket. */
function collectBlocking(detectorKey: string, value: unknown, out: unknown[], seen: Set<object>): void {
  if (!value || typeof value !== 'object') return;
  if (seen.has(value)) return;
  seen.add(value);
  if (Array.isArray(value)) {
    for (const entry of value) collectBlocking(detectorKey, entry, out, seen);
    return;
  }
  if (isTruthViolation(detectorKey, value)) out.push(value);
  for (const entry of Object.values(value as Record<string, unknown>)) {
    collectBlocking(detectorKey, entry, out, seen);
  }
}

export interface QaSeveritySummary {
  /** Findings that make the report NOT decision-usable. */
  blocking: Array<{ detector: string; message: string }>;
  /** Block-severity findings reclassified as advisory by this policy. */
  advisory: Array<{ detector: string; message: string }>;
  /**
   * Findings that ARE in the blocking tier but could not show their work, so
   * they were demoted rather than enforced. Called out separately from
   * `advisory` because this is a DETECTOR defect, not a policy judgement — a
   * non-empty list means something is emitting unverifiable blocks and should
   * be fixed, not tolerated.
   */
  evidenceDowngrades: EvidenceDowngrade[];
}

function messageOf(finding: unknown): string {
  const row = (finding ?? {}) as Record<string, unknown>;
  const raw = row.message ?? row.detail ?? row.kind ?? row.label ?? 'unspecified finding';
  return String(raw).slice(0, 4000);
}

/**
 * Stable evidence identity for one finding.
 *
 * A detector message usually describes a CLASS of failure, not the offending
 * claim. Caveat-honesty findings are the clearest example: every two-band gap
 * says the same thing even when one row is about stakeholder support and
 * another is about a permitting forecast. Deduping by detector + message
 * therefore erased independent truth violations.
 *
 * Prefer the quoted narrative span. `reportFullText()` can project the same
 * sentence both as ordinary prose and as a structured `- Text: ...` row, so
 * strip only that generated wrapper and normalize typography/whitespace. When
 * a detector carries values instead of prose, bind the identity to the fact
 * and both sides of the comparison. Findings without either shape retain the
 * legacy detector + message identity.
 */
function stableEvidenceIdentity(finding: unknown): string {
  if (!finding || typeof finding !== 'object') return '';
  const row = finding as Record<string, unknown>;
  // Caveat findings carry an untruncated `matchText`; prefer it over the
  // 280-character display excerpt so two long claims with the same prefix do
  // not collapse to one attestation identity.
  const quoted = row.sentenceText ?? row.claimText ?? row.matchText ?? row.excerpt;
  if (typeof quoted === 'string' && quoted.trim()) {
    return `text:${quoted
      .replace(/^\s*-\s*(?:text|claim|detail|description):\s*/i, '')
      .replace(/<!--\s*confidence:\s*[^>]+?\s*-->/gi, '')
      .replace(/\[Ref\s+\d+(?:\s*,\s*\d+)*\]/gi, '')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[.!?]+$/, '')
      .toLocaleLowerCase('en-US')}`;
  }

  const values = Array.isArray(row.conflictingValues)
    ? row.conflictingValues.map(String).sort().join('|')
    : '';
  if (row.factId != null || row.observed != null || row.expected != null || values) {
    return [
      `fact:${String(row.factId ?? row.canonicalKey ?? '')}`,
      `observed:${String(row.observed ?? '')}`,
      `expected:${String(row.expected ?? '')}`,
      `conflicts:${values}`,
    ].join('\u0001');
  }
  return '';
}

/**
 * Detector messages are often class-level boilerplate. A legacy attestation
 * id derived from detector + message is safe only while that message identifies
 * one evidence claim. When several distinct claims share it, bind each check
 * to the same stable evidence identity used by the hold deduper. Exact duplicate
 * projections still share one id; independent claims cannot clear each other.
 *
 * Unique messages intentionally retain the legacy id so existing overrides do
 * not churn. A legacy generic override becomes fail-closed only when a rerun
 * presents multiple distinct claims under that same message, where honoring it
 * would make its scope unknowable.
 */
function ambiguousTruthMessages(rows: readonly unknown[]): ReadonlySet<string> {
  const identitiesByMessage = new Map<string, Set<string>>();
  for (const row of rows) {
    const message = messageOf(row);
    const identity = stableEvidenceIdentity(row) || 'unlocatable';
    const identities = identitiesByMessage.get(message) ?? new Set<string>();
    identities.add(identity);
    identitiesByMessage.set(message, identities);
  }
  return new Set(
    [...identitiesByMessage.entries()]
      .filter(([, identities]) => identities.size > 1)
      .map(([message]) => message),
  );
}

function truthCheckIdForFinding(
  detector: string,
  message: string,
  finding: unknown,
  ambiguousMessages: ReadonlySet<string>,
): string {
  if (!ambiguousMessages.has(message)) return stableQuestionId(detector, message);
  return stableQuestionId(
    detector,
    `${message}\u0000${stableEvidenceIdentity(finding) || 'unlocatable'}`,
  );
}

/** A caveat hold reason must name the claim, not repeat detector boilerplate. */
function displayedMessage(detector: string, finding: unknown, base: string): string {
  if (detector !== 'caveatHonesty' || !finding || typeof finding !== 'object') return base;
  const row = finding as Record<string, unknown>;
  const quoted = row.sentenceText ?? row.claimText ?? row.excerpt;
  if (typeof quoted !== 'string' || !quoted.trim()) return base;
  const compact = quoted.replace(/\s+/g, ' ').trim().slice(0, 180);
  return `${base} Claim: "${compact}${quoted.trim().length > 180 ? '\u2026' : ''}"`;
}

/**
 * Classify every block-severity finding in a `qaFindings` object.
 * Unknown detector buckets are advisory — opting a detector into the
 * blocking tier is an explicit edit to this module.
 */
export function summarizeQaSeverity(
  qaFindings: unknown,
  /**
   * Check ids an operator has ATTESTED to (from `SiteAuditOverride`). An
   * attested finding is reclassified as advisory: the operator has recorded
   * that they stand behind the statement, which is knowledge the evidence does
   * not hold. Omitted ⇒ nothing is suppressed, so an un-wired caller fails
   * CLOSED (still blocks) rather than silently clearing findings.
   */
  attestedCheckIds?: ReadonlySet<string>,
): QaSeveritySummary {
  const summary: QaSeveritySummary = { blocking: [], advisory: [], evidenceDowngrades: [] };
  if (!qaFindings || typeof qaFindings !== 'object') return summary;

  // A report can project the same semantic sentence through several fields
  // (for example structuredFindings, contextualKeyFindings, and claims). The
  // detector is right to inspect every reader-facing projection, but the hold
  // reason is one conflict, not one conflict per projection. Collapse ONLY an
  // identical detector + message + stable evidence identity. A generic
  // detector message shared by two different quoted claims is NOT a duplicate.
  const blockingSeen = new Set<string>();
  const advisorySeen = new Set<string>();
  const downgradeSeen = new Set<string>();
  const keyOf = (detector: string, message: string, row: unknown): string =>
    detector + '\u0000' + message + '\u0000' + stableEvidenceIdentity(row);

  for (const [detectorKey, bucket] of Object.entries(qaFindings as Record<string, unknown>)) {
    // Raw review-stage evidence is historical provenance, not the final detector result.
    if (detectorKey === 'sourceRoleReview' || detectorKey === 'finalSourceReview') continue;
    const blockingRows: unknown[] = [];
    collectBlocking(detectorKey, bucket, blockingRows, new Set<object>());
    const ambiguousMessages = ambiguousTruthMessages(blockingRows);

    // Every block-severity row in the bucket, policy-independent — the
    // difference is what this policy downgraded to advisory.
    const anyBlock: unknown[] = [];
    collectAnyBlockSeverity(bucket, anyBlock, new Set<object>());

    for (const row of blockingRows) {
      const rawMessage = messageOf(row);
      const message = displayedMessage(detectorKey, row, rawMessage);
      if (attestedCheckIds?.has(
        truthCheckIdForFinding(detectorKey, rawMessage, row, ambiguousMessages),
      )) {
        // Settled by a human. Surfaced, never silently dropped.
        const key = keyOf(detectorKey, rawMessage, row);
        if (!advisorySeen.has(key)) {
          advisorySeen.add(key);
          summary.advisory.push({ detector: detectorKey, message });
        }
        continue;
      }
      const key = keyOf(detectorKey, rawMessage, row);
      if (!blockingSeen.has(key)) {
        blockingSeen.add(key);
        summary.blocking.push({ detector: detectorKey, message });
      }
    }
    for (const row of anyBlock) {
      if (blockingRows.includes(row)) continue;
      const rawMessage = messageOf(row);
      const message = displayedMessage(detectorKey, row, rawMessage);
      const key = keyOf(detectorKey, rawMessage, row);
      if (!advisorySeen.has(key)) {
        advisorySeen.add(key);
        summary.advisory.push({ detector: detectorKey, message });
      }
      // Distinguish "policy says this is not a truth violation" from "this
      // claims to be one but cannot prove it".
      if (isInBlockingTier(detectorKey, row) && !hasDemonstrableEvidence(row)) {
        if (!downgradeSeen.has(key)) {
          downgradeSeen.add(key);
          summary.evidenceDowngrades.push({ detector: detectorKey, message });
        }
      }
    }
  }
  return summary;
}

/** Collect every `severity: 'block'` row, policy-independent. */
function collectAnyBlockSeverity(value: unknown, out: unknown[], seen: Set<object>): void {
  if (!value || typeof value !== 'object') return;
  if (seen.has(value)) return;
  seen.add(value);
  if (Array.isArray(value)) {
    for (const entry of value) collectAnyBlockSeverity(entry, out, seen);
    return;
  }
  const row = value as Record<string, unknown>;
  if (row.severity === 'block') out.push(value);
  for (const entry of Object.values(row)) collectAnyBlockSeverity(entry, out, seen);
}

/**
 * THE gate predicate — replaces the blanket `containsBlockSeverity` scan.
 * True only when the report asserts something demonstrably untrue.
 */
export function containsTruthViolation(
  qaFindings: unknown,
  attestedCheckIds?: ReadonlySet<string>,
): boolean {
  return summarizeQaSeverity(qaFindings, attestedCheckIds).blocking.length > 0;
}

/* ── Human-resolvable truth questions ─────────────────────────────────── */

/**
 * A truth violation, restated as a question a human can ANSWER (2026-08-05).
 *
 * A detector firing must not be a dead end. Generation completes and the
 * report persists; each truth violation becomes an open question carrying the
 * competing candidate values, so the operator can pick one or supply their
 * own. The answer is written to `sed.amendments`, which
 * `applyUserAmendmentFacts` folds into the canonical facts as a tier-1
 * VERIFIED source — so the resolution persists into the prompt, the grounding
 * gate, the reviewer's ground truth and every future report from this SED,
 * and the question does not come back.
 */
export interface TruthQuestion {
  /** Stable id — same violation on a rerun yields the same id. */
  id: string;
  /**
   * What kind of act settles this question.
   *
   * `'value'` — the finding names a fact with a writable SED path, so the
   * answer is a corrected VALUE (internalNumber, grounding).
   *
   * `'attestation'` — `absenceAssertionAudit` and `caveatHonesty` findings name
   * no fact, and their questions are not value questions anyway: "Redundancy
   * has not been confirmed" is not settled by typing a number, it is settled by
   * an operator who knows something the evidence does not recording that they
   * stand behind it. Persisted as a `SiteAuditOverride` (`type: 'validate'`)
   * keyed by `id`, which is stable across reruns so the answer sticks.
   *
   * Before this split every absence and caveat block was a DEAD END: the dialog
   * could raise the question but had nowhere to put the answer.
   */
  kind: 'value' | 'attestation';
  /** Canonical fact this question settles, when the detector names one. */
  factId?: string;
  detector: string;
  /** What the report currently asserts. */
  reportClaim: string;
  /** Human-readable question for the resolution prompt. */
  question: string;
  /** Values the operator can choose between; may be empty (free entry). */
  candidates: Array<{ value: string; label: string }>;
  /** True when the operator may type a value instead of picking one. */
  allowFreeText: boolean;
}

function stableQuestionId(detector: string, message: string): string {
  let hash = 0;
  const basis = `${detector}|${message}`;
  for (let i = 0; i < basis.length; i += 1) {
    hash = ((hash << 5) - hash + basis.charCodeAt(i)) | 0;
  }
  return `truth-${detector}-${Math.abs(hash).toString(36)}`;
}

/** Pull candidate values out of a finding's own payload, when it carries them. */
export function formatCandidateLabelWithValue(label: string, value: string): string {
  const cleanLabel = label.trim();
  const cleanValue = value.trim();
  if (!cleanValue || cleanLabel.includes(cleanValue)) return cleanLabel;
  return `${cleanLabel}: ${cleanValue}`;
}

function candidatesFrom(finding: Record<string, unknown>): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];
  const push = (raw: unknown, label: string): void => {
    if (raw === null || raw === undefined || raw === '') return;
    const value = String(raw);
    if (out.some((c) => c.value === value)) return;
    out.push({ value, label: formatCandidateLabelWithValue(label, value) });
  };
  // Grounding value-mismatch carries the permitted canonical value.
  push(finding.expected, 'Canonical evidence value');
  push(finding.observed, 'Value stated in the report');
  // internalNumber conflicts carry every competing figure.
  if (Array.isArray(finding.conflictingValues)) {
    for (const value of finding.conflictingValues) push(value, 'Stated in the report');
  }
  return out;
}

/**
 * Convert the blocking truth violations in a `qaFindings` object into
 * answerable questions. Pure; returns `[]` when the report is clean.
 */
/**
 * R-67 canonical key → governed factId. THE THIRD NAMESPACE.
 *
 * A truth question is only answerable if its `factId` resolves to a writable
 * SED path (`factIdToSedPath`). `InternalNumberFinding.canonicalKey` is none of
 * those things — it is R-67's own vocabulary (`interconnection_distance_km`),
 * distinct from both the evidence layer's `CanonicalFactKey`
 * (`interconnectionDistanceKm`, via `canonicalFactKeyFromInternal`) and the
 * descriptor layer's factId (`substationDistKm`). Stamping it raw produced a
 * question the resolution dialog could not accept an answer for — the exact
 * dead end the truth-question loop exists to remove. Measured 2026-08-05: every
 * internalNumber block on the real-body corpus was unanswerable.
 *
 * A literal, not an import: this module is deliberately dependency-free and
 * total (see the header), and importing `buildCanonicalFactStore` would drag
 * the whole resolver graph into the severity policy.
 *
 * Only FAITHFUL mappings belong here. An unmapped key keeps today's behaviour —
 * the question is raised but marked unanswerable — which is honest. Guessing a
 * near-miss target would write the operator's correction to the wrong field.
 * `wetland_percent` (a percentage, whereas `wetlands.acres` is an area),
 * `water_source_type` and `census_tract` have no faithful target and are
 * deliberately absent.
 */
const INTERNAL_KEY_TO_FACT_ID: Readonly<Record<string, string>> = {
  capacity_mw: 'power.declaredMw',
  // R-67 labels this "Interconnection / substation distance", conflating the
  // grid substation spur with connectivity's `ix.distanceKm` (an INTERNET
  // EXCHANGE distance — a different quantity that merely shares the unit).
  // The substation is the one this finding is actually about.
  interconnection_distance_km: 'substationDistKm',
  nearest_substation: 'grid.substationName',
  nuclear_proximity_km: 'reactor.distanceKm',
  acreage: 'siteArea.acres',
  voltage_kv: 'grid.voltageKv',
  flood_zone: 'flood.zone',
  grid_operator: 'electricUtility.name',
};

/**
 * Canonical keys whose value is COMPUTED BY AN ENGINE, not held by the SED.
 *
 * `answerableFactId` falls back to the raw `canonicalKey` when no faithful
 * mapping exists, which makes the question a `'value'` question — and a value
 * answer is written to `sed.amendments`. For a financial figure that is the
 * wrong affordance and a dead end twice over: the SED has no slot for
 * `lcoe_usd_per_mwh`, and even if it did, the number is recomputed from
 * `FinancialAssumptions` on every re-stamp, so the amendment would be
 * overwritten. "Amend the SED" cannot settle a fact the SED does not own.
 *
 * These route to an ATTESTATION instead — the operator records whether they
 * stand behind the figure — and the copy names the real remedy: correct the
 * assumptions and let the engine re-stamp.
 *
 * A literal for the same reason `INTERNAL_KEY_TO_FACT_ID` is one: this module
 * is deliberately dependency-free and total. Mirrors `FinancialCanonicalKey`
 * in `audit/internalNumberReconciler.ts`; keep the two in step.
 */
const ENGINE_SOURCED_FINANCIAL_KEYS: ReadonlySet<string> = new Set([
  'lcoe_usd_per_mwh',
  'npv_usd',
  'capex_total_usd',
  'capex_per_kw_usd',
  'irr_pct',
  'hurdle_rate_pct',
  'wacc_pct',
  'capacity_factor_pct',
  'payback_years',
  'annual_mwh',
]);

/** True when the finding's subject is an engine-computed financial figure. */
function isEngineSourcedFinancial(finding: Record<string, unknown>): boolean {
  return typeof finding.canonicalKey === 'string'
    && ENGINE_SOURCED_FINANCIAL_KEYS.has(finding.canonicalKey);
}


/** Resolve the answerable factId for a finding, bridging R-67's vocabulary. */
function answerableFactId(finding: Record<string, unknown>): string | undefined {
  if (typeof finding.factId === 'string') return finding.factId;
  if (typeof finding.canonicalKey === 'string') {
    return INTERNAL_KEY_TO_FACT_ID[finding.canonicalKey] ?? finding.canonicalKey;
  }
  return undefined;
}

/**
 * The two ways an operator settles an attestation.
 *
 * `confirmed` records that they stand behind the statement despite the
 * evidence not supporting it — knowledge the SED does not hold. `retract`
 * concedes the detector and sends the report back for regeneration; it
 * deliberately does NOT clear the finding.
 */
export const ATTESTATION_CONFIRMED = 'confirmed';
export const ATTESTATION_RETRACT = 'retract';
const ATTESTATION_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: ATTESTATION_CONFIRMED, label: 'Yes — it is accurate, and I stand behind it' },
  { value: ATTESTATION_RETRACT, label: 'No — regenerate this report' },
];

/**
 * Attestation choices for an engine-computed figure. Same persisted VALUES as
 * `ATTESTATION_CHOICES` (so the override path is unchanged), different labels
 * — "regenerate this report" does not tell a reader that the figure follows
 * from assumptions they can edit.
 */
const ENGINE_FIGURE_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: ATTESTATION_CONFIRMED, label: 'Yes — the assumptions behind it are right' },
  { value: ATTESTATION_RETRACT, label: 'No — correct the financial assumptions and re-stamp' },
];

/**
 * Stable check id for an attestation, matching the question id so an override
 * recorded against a question suppresses exactly that finding on a rerun.
 * Exported so the persistence layer cannot derive a different key.
 */
export function truthCheckId(detector: string, message: string): string {
  return stableQuestionId(detector, message);
}

export function buildTruthQuestions(
  qaFindings: unknown,
  attestedCheckIds?: ReadonlySet<string>,
): TruthQuestion[] {
  if (!qaFindings || typeof qaFindings !== 'object') return [];
  const questions: TruthQuestion[] = [];
  const seen = new Set<string>();

  for (const [detectorKey, bucket] of Object.entries(qaFindings as Record<string, unknown>)) {
    // Raw review-stage evidence is historical provenance, not the final detector result.
    if (detectorKey === 'sourceRoleReview' || detectorKey === 'finalSourceReview') continue;
    const rows: unknown[] = [];
    collectBlocking(detectorKey, bucket, rows, new Set<object>());
    const ambiguousMessages = ambiguousTruthMessages(rows);
    for (const row of rows) {
      const finding = row as Record<string, unknown>;
      const message = messageOf(finding);
      // A unique message retains its legacy id. If several distinct claims
      // share detector boilerplate, bind the id to this exact evidence span so
      // one attestation cannot suppress all of them.
      const quotedSpan = [finding.excerpt, finding.sentenceText, finding.claimText].find(
        (v): v is string => typeof v === 'string' && v.trim().length > 0,
      );
      const id = truthCheckIdForFinding(detectorKey, message, row, ambiguousMessages);
      if (seen.has(id)) continue;
      if (attestedCheckIds?.has(id)) continue;   // already settled by a human
      seen.add(id);
      // An engine-computed financial figure is never a value question — see
      // ENGINE_SOURCED_FINANCIAL_KEYS. Checked BEFORE answerableFactId so the
      // raw-key fallback cannot promote it to `'value'`.
      const engineFigure = isEngineSourcedFinancial(finding);
      const rawFactId = engineFigure ? undefined : answerableFactId(finding);
      const kind: TruthQuestion['kind'] = rawFactId ? 'value' : 'attestation';
      const rawClaim = quotedSpan ?? message;
      const cleanClaim = rawClaim.replace(/<!--[\s\S]*?-->/g, '').replace(/[ \t]{2,}/g, ' ').trim();
      const rawCandidates = kind === 'value' ? candidatesFrom(finding) : [];
      const semanticBinding = rawFactId
        ? validateSemanticFactBinding({
            factId: rawFactId,
            claimText: cleanClaim || rawClaim,
            observedText: typeof finding.observed === 'string' ? finding.observed : undefined,
            expectedText: typeof finding.expected === 'string' ? finding.expected : undefined,
            message,
            candidateValues: rawCandidates.map((c) => `${c.label} ${c.value}`),
          })
        : { ok: true as const };
      const factId = semanticBinding.ok ? rawFactId : undefined;
      questions.push({
        id,
        kind,
        ...(factId ? { factId } : {}),
        detector: detectorKey,
        reportClaim: cleanClaim || rawClaim,
        question: kind === 'value'
          ? factId
            ? `What is the correct value for "${factId}"?`
            : `Contextual figure cannot be mapped to "${rawFactId}" (${semanticBinding.ok ? 'unbound' : semanticBinding.reason})`
          : engineFigure
            ? 'This figure is computed by the project-finance engine from the '
              + 'financial assumptions, not held in the SED. Is it correct?'
            : 'Is this statement correct?',
        candidates: kind === 'value'
          ? factId ? rawCandidates : []
          : engineFigure ? [...ENGINE_FIGURE_CHOICES] : [...ATTESTATION_CHOICES],
        allowFreeText: Boolean(kind === 'value' && factId),
      });
    }
  }
  return questions;
}

/** Locatable raw truth rows, deduped with the existing severity summary identity. */
export function collectTruthBlockingRows(qaFindings: unknown): Array<{ detector: string; key: string; row: Record<string, unknown> }> {
  if (!qaFindings || typeof qaFindings !== 'object') return [];
  const seen = new Set<string>();
  const out: Array<{ detector: string; key: string; row: Record<string, unknown> }> = [];
  for (const [detector, bucket] of Object.entries(qaFindings)) {
    if (['sourceRoleReview', 'finalSourceReview', 'finalRepairPass', 'reliabilityMarkerCorrections'].includes(detector)) continue;
    const rows: unknown[] = [];
    collectBlocking(detector, bucket, rows, new Set<object>());
    for (const raw of rows) {
      const key = detector + '\u0000' + messageOf(raw) + '\u0000' + stableEvidenceIdentity(raw);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ detector, key, row: raw as Record<string, unknown> });
    }
  }
  return out;
}
